import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { BranchInfosManagement } from '../components/BranchInfosManagement';
import { branchSchema, TBranchSchema } from '@/schemas/branch/branch.schema';
import { useGetBranchById, useUpdateBranch } from '@/hooks/branch.hooks';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { useTranslation } from 'react-i18next';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { useGetTab } from '@/hooks/tab';
import { EditBranchTab } from '@/type/ui/tab/branchTab';
import { BranchWhiteLabelManagement } from '../components/BranchWhiteLabelManagement';
import { getLanguage } from '@/utils/language.utils';

export const EditBranchPage = () => {
  const { t } = useTranslation('branch');
  const { id } = useParams();
  const { data, isLoading } = useGetBranchById(`${id}`);
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditBranchTab.INFOS,
  });

  const {
    watch: watchBranch,
    control: branchControl,
    register: registerBranch,
    setValue: setBranchValue,
    setError: setBranchError,
    handleSubmit: handleSubmitBranch,
    formState: { errors: branchErrors, isSubmitting: isSubmittingBranch },
  } = useForm<TBranchSchema>({
    resolver: zodResolver(branchSchema),
  });

  const { mutate: mutateUpdateBranch, isPending: isPendingUpdateBranch } =
    useUpdateBranch({
      t,
      handleSuccess: () => navigate('/branches'),
    });

  const onSubmitBranchInfos = (branch: TBranchSchema) => {
    const { languageCode } = getLanguage();

    const branchWithId = {
      id,
      ...branch,
      languageCode,
    };
    mutateUpdateBranch(branchWithId);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/branches'}
        title={t('theBranch')}
        subTitle={data?.data?.name}
        subTitleIconClassName="ki-abstract-41"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('branchList')} linkTo="/branches" />
            <BreadcrumbItem text={data?.data?.name} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('branchNotFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditBranchTab.INFOS}
              onChange={() => navigateToTab(EditBranchTab.INFOS)}
            />
            {data?.data?.company?.group?.isWhiteLabel && (
              <TabItem
                text={t('common:infosProPart')}
                leftIcon={<i className="ki-outline ki-copy-success" />}
                active={currentTab === EditBranchTab.WHITE_LABEL}
                onChange={() => navigateToTab(EditBranchTab.WHITE_LABEL)}
              />
            )}
          </TabList>
          {currentTab === EditBranchTab.INFOS && (
            <div className="flex flex-col gap-12">
              <BranchInfosManagement<TBranchSchema>
                t={t}
                data={!isLoading && data?.data}
                watch={watchBranch}
                control={branchControl}
                register={registerBranch}
                setValue={setBranchValue}
                setError={setBranchError}
                errors={branchErrors}
                submitDisabled={isSubmittingBranch || isPendingUpdateBranch}
                onSubmit={handleSubmitBranch(onSubmitBranchInfos)}
              />
            </div>
          )}
          {currentTab === EditBranchTab.WHITE_LABEL && (
            <BranchWhiteLabelManagement<TBranchSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchBranch}
              control={branchControl}
              register={registerBranch}
              setValue={setBranchValue}
              setError={setBranchError}
              errors={branchErrors}
              submitDisabled={isSubmittingBranch || isPendingUpdateBranch}
              onSubmit={handleSubmitBranch(onSubmitBranchInfos)}
            />
          )}
        </div>
      )}
      {(isLoading || isSubmittingBranch || isPendingUpdateBranch) && <Loader />}
    </>
  );
};
